import {
    ChangeDetectionStrategy,
    Component, DestroyRef,
    EventEmitter, HostListener, inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {UntypedFormArray, UntypedFormGroup, UntypedFormControl} from '@angular/forms';
import {FormlyFormOptions, FormlyFieldConfig} from '@ngx-formly/core';
import {Subject, merge, BehaviorSubject, Observable} from 'rxjs';
import {debounceTime, filter, map} from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';

import {SearchFilter} from '@emm/core/models/search-filter';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {environment} from '@env';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'emm-search-filter',
    styleUrls: ['./search-filter.component.scss'],
    templateUrl: './search-filter.component.html',
    standalone: false
})
export class SearchFilterComponent implements OnChanges, OnInit {

    @Input() fields: FormlyFieldConfig[];
    @Input() addDialog: boolean;
    @Input() displaySearchWithOptions: boolean;
    @Input() filteredOptions: Observable<any[]>;
    @Input() searchLabel: any;
    @Input() showMenu: boolean;
    @Input() isAdminOrAuthor: boolean;
    @Input() isSuperAdmin: boolean;
    @Input() componentName: string;
    @Output() filter = new EventEmitter<SearchFilter>();
    @Output() changeFilterType = new EventEmitter<boolean>();
    @Output() add = new EventEmitter();
    @Output() addStandard = new EventEmitter();
    @Output() addCaptive = new EventEmitter();
    @Output() selected = new EventEmitter();

    model: object;
    form: UntypedFormArray;
    searchForm = new UntypedFormControl();
    selectedFields: FormlyFieldConfig[][];
    options: FormlyFormOptions[];

    index = 0;
    dropdownCtrl = new UntypedFormControl();
    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    changes$ = new Subject<Record<string, string | string[]>>();
    canAdd: boolean;
    env = environment;
    showAdd$ = new BehaviorSubject<boolean>(true);

    @HostListener('document:keypress', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    constructor() {}

    ngOnChanges(changes: SimpleChanges): void {
        this.isSuperAdmin ? this.canAdd = true : this.canAdd = this.isAdminOrAuthor;
        if (changes['fields'] && this.hasFields) {
            this.form = new UntypedFormArray(this.fields.map(() => new UntypedFormGroup({})));
            this.model = this.fields.map(() => ({}));
            this.selectedFields = this.fields.map((field) => [field]);
            this.options = this.fields.map(() => ({} as FormlyFormOptions));
            const nameField = this.fields.find(
                (config) => (config.key as string).toLowerCase() === 'name'
            );
            this.dropdownCtrl.setValue(nameField ? this.fields.indexOf(nameField) : this.index);
        }
    }

    ngOnInit(): void {
        this.dropdownCtrl.valueChanges
            .pipe(filter(RA.isNotNil), takeUntilDestroyed(this.destroyRef))
            .subscribe((val: number) => (this.index = val));

        const change$ = this.changes$.pipe(
            filter(RA.isNotNil),
            filter((change: Record<string, string | string[]>) => Object.keys(change).length === 1),
            map(
                (change: Record<string, string | string[]>) =>
                    ({
                        key: Object.keys(change)[0],
                        value: change[Object.keys(change)[0]],
                    } as SearchFilter)
            ),
            takeUntilDestroyed(this.destroyRef)
        );

        const search$ = change$.pipe(
            debounceTime(500)
        );

        const clear$ = change$.pipe(filter((searchFilter) => R.isNil(searchFilter.value)));

        merge(search$, clear$)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((change: SearchFilter) => this.filter.emit(change));

        this.searchForm.valueChanges
            .pipe(filter(RA.isNotNil), takeUntilDestroyed(this.destroyRef))
            .subscribe((val) => {
                const fieldWithFilter = {
                    key: this.fields[this.index].templateOptions.label,
                    value: val
                }
                typeof val === 'object'
                    ? this.selected.emit(val)
                    : this.filter.emit(fieldWithFilter);
            });

        if ((this.env.name === 'test' || this.env.name === 'stg' || this.env.name === 'prod') &&
            (this.componentName === 'item-types' || this.componentName === 'attributes' ||
                this.componentName === 'attribute-groups')) {
            this.showAdd$.next(false);
        } else {
            this.showAdd$.next(true);
        }
    }

    modelChanged(event: Record<string, string | string[]>) {
        if (this.dropdownCtrl.value === 1) {
            this.changeFilterType.emit(true);
        }
        this.changes$.next(event);
    }

    get hasFields() {
        return RA.isNonEmptyArray(this.fields);
    }

    clearSearchTerm() {
        this.searchForm.setValue('');
    }

    autoCompleteDisplay(selected: any) {
        return selected && selected.name;
    }
}
